import React, { useState, useEffect } from "react";
import TournamentHeader from "./TournamentHeader";
import StandingsTable from "./StandingsTable";
import MatchesTable from "./MatchesTable";
import PlayoffBracket from "./PlayoffBracket";
// import mockData from "../data/mockData";
import { fetchSheetData } from "../services/sheetsService";

const TournamentApp = () => {
  // const [data, setData] = useState(mockData); // Sử dụng mockData làm dữ liệu mặc định
  const [data, setData] = useState({});
  const [lastUpdated, setLastUpdated] = useState(
    new Date().toLocaleTimeString()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState("groupA");
  const [error, setError] = useState(null);

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Hàm lấy dữ liệu từ Google Sheets
  const refreshData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Lấy dữ liệu từ Google Sheets
      const sheetsData = await fetchSheetData();

      // Cập nhật state với dữ liệu mới
      setData(sheetsData);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("Failed to fetch data from Google Sheets:", err);
      setError(
        "Không thể lấy dữ liệu từ Google Sheets. Đang sử dụng dữ liệu mẫu."
      );
      // Sử dụng mockData nếu không lấy được dữ liệu từ API
      // setData(mockData);
    } finally {
      setIsLoading(false);
    }
  };

  // Gọi refreshData khi component mount
  useEffect(() => {
    refreshData();

    // Thiết lập interval để cập nhật dữ liệu mỗi 30 giây
    const interval = setInterval(refreshData, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto p-4">
        <TournamentHeader
          tournamentInfo={data.tournamentInfo}
          onRefresh={refreshData}
          lastUpdated={lastUpdated}
          isLoading={isLoading}
        />

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}

        {/* Rest of the component remains the same */}

        {isMobile ? (
          // Mobile view with tabs
          <>
            <div className="tabs flex mb-4 overflow-x-auto mt-4">
              <button
                className={`px-4 py-2 whitespace-nowrap ${
                  activeTab === "groupA"
                    ? "text-blue-700 border-b-2 border-blue-700 font-medium"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("groupA")}
              >
                Bảng A
              </button>
              <button
                className={`px-4 py-2 whitespace-nowrap ${
                  activeTab === "groupB"
                    ? "text-blue-700 border-b-2 border-blue-700 font-medium"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("groupB")}
              >
                Bảng B
              </button>
              <button
                className={`px-4 py-2 whitespace-nowrap ${
                  activeTab === "playoffs"
                    ? "text-blue-700 border-b-2 border-blue-700 font-medium"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("playoffs")}
              >
                Vòng chung kết
              </button>
            </div>

            {activeTab === "groupA" && (
              <div>
                <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
                  <StandingsTable
                    standings={data.groups?.["A"]?.standings}
                    teams={data.groups?.["A"]?.teams}
                    group="A"
                  />
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <MatchesTable
                    matches={data?.groups?.["A"]?.matches}
                    teams={data.groups?.["A"]?.teams}
                    group="A"
                  />
                </div>
              </div>
            )}

            {activeTab === "groupB" && (
              <div>
                <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
                  <StandingsTable
                    standings={data.groups?.["B"]?.standings}
                    teams={data.groups?.["B"]?.teams}
                    group="B"
                  />
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <MatchesTable
                    matches={data.groups?.["B"]?.matches}
                    teams={data.groups?.["B"]?.teams}
                    group="B"
                  />
                </div>
              </div>
            )}

            {activeTab === "playoffs" && (
              <div className="bg-white rounded-lg shadow p-4">
                <PlayoffBracket playoffs={data.playoffs} />
              </div>
            )}
          </>
        ) : (
          // Desktop view with vertical layout
          <div className="flex flex-col space-y-6 mt-6">
            {/* Standings - Full Width */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <StandingsTable
                  standings={data.groups?.["A"]?.standings}
                  teams={data.groups?.["A"]?.teams}
                  group="A"
                />
              </div>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <StandingsTable
                  standings={data.groups?.["B"]?.standings}
                  teams={data.groups?.["B"]?.teams}
                  group="B"
                />
              </div>
            </div>

            {/* Matches - Group A - Full Width */}
            <div className="bg-white rounded-lg shadow p-4">
              <MatchesTable
                matches={data.groups?.["A"]?.matches}
                teams={data.groups?.["A"]?.teams}
                group="A"
              />
            </div>

            {/* Matches - Group B - Full Width */}
            <div className="bg-white rounded-lg shadow p-4">
              <MatchesTable
                matches={data.groups?.["B"]?.matches}
                teams={data.groups?.["B"]?.teams}
                group="B"
              />
            </div>

            {/* Playoffs - Full Width */}
            <div className="bg-white rounded-lg shadow p-4">
              <PlayoffBracket playoffs={data.playoffs} />
            </div>
          </div>
        )}

        <footer className="text-center text-gray-500 text-sm mt-8 pb-4">
          <p>© {new Date().getFullYear()} VGDA Pickleball Tournament</p>
          <p className="mt-1">Tổ chức bởi Fairplay Sports</p>
        </footer>

        {/* ... */}
      </div>
    </div>
  );
};

export default TournamentApp;
